import React, { useEffect, useState } from 'react';
import { displayDate, isAccessError, loadMonitoring } from './monitoring-api';

export default function LiveKitRoomLogs({ roomId, user, onAccessError }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [level, setLevel] = useState('error');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    let timer;
    let cursor = '';
    setFeed(null);
    setError('');
    async function listen() {
      let delay = 100;
      try {
        const next = await loadMonitoring(`logs?roomId=${encodeURIComponent(roomId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, user, controller.signal);
        if (controller.signal.aborted) return;
        if (!Array.isArray(next.entries) || typeof next.cursor !== 'string') throw new Error('The log feed returned an invalid response. Retrying…');
        cursor = next.cursor;
        setFeed(next);
        setError('');
      } catch (failure) {
        if (controller.signal.aborted) return;
        if (isAccessError(failure)) { onAccessError(failure); return; }
        setError(failure.message);
        delay = 3000;
      }
      if (!controller.signal.aborted) timer = setTimeout(listen, delay);
    }
    void listen();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [roomId, user, onAccessError]);

  const query = search.trim().toLowerCase();
  const entries = (feed?.entries || []).filter((entry) =>
    (level === 'all' || entry.severity === 'error' || (level === 'warning' && entry.severity === 'warning')) &&
    [entry.kind, entry.message, entry.source, entry.reasonCode, entry.provider, entry.avaId, entry.trackSid].filter(Boolean).join(' ').toLowerCase().includes(query));

  return <div className="monitor-logs">
    <div className="monitor-log-toolbar">
      <div className="monitor-log-filter"><label htmlFor="monitor-log-level">Show</label><select id="monitor-log-level" value={level} onChange={(event) => setLevel(event.target.value)}>
        <option value="error">Errors</option><option value="warning">Errors and warnings</option><option value="all">All events</option>
      </select></div>
      <div className="monitor-log-filter"><label htmlFor="monitor-log-search">Find a log</label><input id="monitor-log-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Event, reason or device…" /></div>
      <span className={`monitor-feed-status ${error ? 'monitor-stale' : ''}`} role="status">{error ? 'Reconnecting…' : feed ? 'Live · waiting for events' : 'Connecting to logs…'}</span>
    </div>
    <p className="monitor-muted">Room, client and transcription events reported to Firebase. New events appear automatically; browser console logs are not included.</p>
    {error && <p role="alert" className="monitor-error">{error} {feed && 'Showing the last received logs.'}</p>}
    {feed?.truncated && <p className="monitor-note">Showing recent events only. Older events are outside this feed.</p>}
    {feed && !entries.length && <p className="monitor-empty">{feed.entries.length ? 'No logs match these filters.' : 'No events reported for this conversation yet.'}</p>}
    <ol className="monitor-log-list" aria-label="Conversation logs">
      {entries.map((entry) => <li key={entry.id}>
        <time dateTime={new Date(entry.atTs).toISOString()}>{displayDate(entry.atTs)}</time>
        <span className={`monitor-severity monitor-severity-${entry.severity}`}>{entry.severity}</span>
        <div><strong>{entry.message || entry.kind}</strong><span className="monitor-log-context">{[entry.source, entry.reasonCode, entry.provider, entry.avaId, entry.trackSid].filter(Boolean).join(' · ')}</span></div>
      </li>)}
    </ol>
  </div>;
}
